import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {useGetUser} from '@/features/users/queries/useGetUser'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {useTreatmentRequest} from '@/features/treatment-requests/services/useTreatmentRequest'

interface UseBreadcrumbEntityNameProps {
    routePath: string
    entityId?: string
}

export const useBreadcrumbEntityName = ({routePath, entityId}: UseBreadcrumbEntityNameProps) => {
    // Only try to resolve entity names for main detail routes, not subroutes
    // Main detail routes are those that end with /:id or have a specific pattern

    // Check if this is a main patient detail route (not a subroute)
    const isMainPatientRoute = routePath === '/patients/:id'

    // Check if this is a main user detail route (not a subroute)
    const isMainUserRoute = routePath === '/users/:id'

    // Check if this is a main project detail route (not a subroute)
    const isMainProjectRoute = routePath === '/projects/:id'

    // Check if this is a main treatment request detail route (not a subroute)
    const isMainTreatmentRequestRoute = routePath === '/treatment-requests/:id'

    // Fetch patient data if it's a main patient route
    const patientQuery = useGetPatient({
        patientId: isMainPatientRoute ? entityId : undefined,
        responseType: 'small',
        options: {
            enabled: isMainPatientRoute && !!entityId
        }
    })

    // Fetch user data if it's a main user route
    const userQuery = useGetUser({
        userId: isMainUserRoute ? entityId : undefined,
        responseType: 'small',
        options: {
            enabled: isMainUserRoute && !!entityId
        }
    })

    // Fetch project data if it's a main project route
    const projectQuery = useGetProject({
        urlParams: {
            projectId: isMainProjectRoute ? entityId ?? '' : ''
        },
        params: {
            responseType: 'small'
        },
        options: {
            enabled: isMainProjectRoute && !!entityId
        }
    })

    // Fetch treatment request data if it's a main treatment request route
    const treatmentRequestQuery = useTreatmentRequest(isMainTreatmentRequestRoute ? entityId : undefined, {
        enabled: isMainTreatmentRequestRoute && !!entityId
    })

    // Return the appropriate data based on route type
    if (isMainPatientRoute) {
        return {
            name: patientQuery.data?.fullName,
            isLoading: patientQuery.isLoading,
            isError: patientQuery.isError
        }
    }

    if (isMainUserRoute) {
        return {
            name: userQuery.data?.fullName,
            isLoading: userQuery.isLoading,
            isError: userQuery.isError
        }
    }

    if (isMainProjectRoute) {
        return {
            name: projectQuery.data?.code
                ? `N°${projectQuery.data?.code} - ${projectQuery.data.patient.fullName}`
                : undefined,
            isLoading: projectQuery.isLoading,
            isError: projectQuery.isError
        }
    }

    if (isMainTreatmentRequestRoute) {
        return {
            name: treatmentRequestQuery.data?.code
                ? `N°${treatmentRequestQuery.data?.code} - ${treatmentRequestQuery.data?.patientFullName}`
                : undefined,
            isLoading: treatmentRequestQuery.isLoading,
            isError: treatmentRequestQuery.isError
        }
    }

    return {
        name: undefined,
        isLoading: false,
        isError: false
    }
}
